// Charges récupérables, TEOM et régularisation (F-055, F-056, D-17) : proposition explicable, validée avant envoi.
import { useState } from "react";
import { del, post, upload } from "@core/api";
import { useGet } from "@core/hooks";
import { date, money, today } from "@core/format";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, FileDrop, Input, KeyValues, Loading, MoneyInput, Notice, PageHeader, Select, Table, Tabs, fieldError } from "@core/ui";
import { Amount, DocLink, LeaseSelect, errMessage, useAction, useG, type LeaseRef } from "./shared";

type Overview = {
  year: number;
  categories: { value: string; label: string }[];
  keys: { value: string; label: string }[];
  expenses: any[];
  teom: any[];
  regularisations: any[];
  properties: { id: string; label: string; kind: string; building_id: string | null; building_name: string | null; surface_m2: number | null }[];
  buildings: { id: string; name: string }[];
  leases: (LeaseRef & { tenants: string })[];
  sources: { id: string; label: string; url: string }[];
};

const REG_STATUS: Record<string, string> = { brouillon: "Proposition à valider", validee: "Validée", envoyee: "Envoyée", annulee: "Annulée" };

function yearOptions() {
  const y = new Date().getFullYear();
  return Array.from({ length: 7 }, (_, i) => String(y - i)).map((v) => ({ value: v, label: v }));
}

export function ChargesPage() {
  const { g } = useG();
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [tab, setTab] = useState<"depenses" | "teom" | "regularisations">("regularisations");
  const q = useGet<Overview>(g(`/charges?year=${year}`));
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Charges et régularisation"
        intro="Saisissez les dépenses récupérables justifiées, répartissez-les entre logements, puis comparez la part de chaque locataire aux provisions appelées. Des charges au forfait ne se régularisent pas."
        actions={<Select label="Exercice" value={String(year)} onChange={(e) => setYear(Number(e.target.value))} options={yearOptions()} />}
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { value: "regularisations", label: "Régularisations" },
          { value: "depenses", label: "Dépenses récupérables" },
          { value: "teom", label: "TEOM" },
        ]}
      />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {q.data ? (
        <>
          {tab === "depenses" ? <Expenses d={q.data} /> : null}
          {tab === "teom" ? <Teom d={q.data} /> : null}
          {tab === "regularisations" ? <Regularisations d={q.data} /> : null}
          <p className="small muted">
            Sources :{" "}
            {q.data.sources.map((s, i) => (
              <span key={s.id}>
                {i ? " · " : ""}
                <a href={s.url} target="_blank" rel="noreferrer">
                  {s.label}
                </a>
              </span>
            ))}
          </p>
        </>
      ) : null}
    </div>
  );
}

async function uploadJustificatif(orgId: string, file: File, category: string, propertyId?: string): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  form.append("category", category);
  form.append("title", file.name);
  if (propertyId) form.append("property_id", propertyId);
  const doc = await upload<{ id: string }>(`/api/v1/orgs/${orgId}/documents`, form);
  return doc.id;
}

function Expenses({ d }: { d: Overview }) {
  const { g, can, orgId } = useG();
  const { busy, run, error } = useAction();
  const y = d.year;
  const [f, setF] = useState({ target: "logement", property_id: "", building_id: "", period_start: `${y}-01-01`, period_end: `${y}-12-31`, category: "parties_communes", label: "", key: "tantiemes", note: "" });
  const [amount, setAmount] = useState<number | null>(null);
  const [recoverable, setRecoverable] = useState<number | null>(null);
  const [weights, setWeights] = useState<Record<string, string>>({});
  const [doc, setDoc] = useState<{ id: string; name: string } | null>(null);
  const [upErr, setUpErr] = useState<string | null>(null);
  const buildingProps = d.properties.filter((p) => p.building_id === f.building_id && p.kind !== "cave" && p.kind !== "parking");
  const needWeights = f.target === "immeuble" && (f.key === "tantiemes" || f.key === "compteur");
  return (
    <>
      {can("finance") ? (
        <Card title="Ajouter une dépense">
          <div className="form-row">
            <Select label="Rattachement" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} options={[{ value: "logement", label: "Un logement (montant déjà ventilé)" }, { value: "immeuble", label: "Un immeuble (à répartir)" }]} />
            {f.target === "logement" ? (
              <Select label="Logement" value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })} options={[{ value: "", label: "Choisir" }, ...d.properties.map((p) => ({ value: p.id, label: p.label }))]} />
            ) : (
              <Select label="Immeuble" value={f.building_id} onChange={(e) => setF({ ...f, building_id: e.target.value })} options={[{ value: "", label: "Choisir" }, ...d.buildings.map((b) => ({ value: b.id, label: b.name }))]} />
            )}
          </div>
          <div className="form-row">
            <Select label="Catégorie (décret n° 87-713)" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} options={d.categories} error={fieldError(error, "category")} />
            <Input label="Libellé" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} error={fieldError(error, "label")} />
          </div>
          <div className="form-row">
            <Input label="Début de période" type="date" value={f.period_start} onChange={(e) => setF({ ...f, period_start: e.target.value })} />
            <Input label="Fin de période" type="date" value={f.period_end} onChange={(e) => setF({ ...f, period_end: e.target.value })} error={fieldError(error, "period_end")} />
          </div>
          <div className="form-row">
            <MoneyInput label="Montant de la dépense" cents={amount} onChange={setAmount} error={fieldError(error, "amount_cents")} />
            <MoneyInput label="Dont part récupérable" cents={recoverable} onChange={setRecoverable} error={fieldError(error, "recoverable_cents")} help="Seule cette part est imputée aux locataires." />
          </div>
          {f.target === "immeuble" ? (
            <>
              <Select label="Clé de répartition" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} options={d.keys.filter((k) => k.value !== "lot_unique")} error={fieldError(error, "distribution_key")} />
              {needWeights ? (
                <div className="grid-3">
                  {buildingProps.map((p) => (
                    <Input key={p.id} label={`${f.key === "tantiemes" ? "Tantièmes" : "Consommation relevée"} — ${p.label}`} type="number" min={0} value={weights[p.id] ?? ""} onChange={(e) => setWeights({ ...weights, [p.id]: e.target.value })} />
                  ))}
                </div>
              ) : null}
              {f.key === "surface" ? <p className="small muted">Surfaces lues sur les fiches logement : {buildingProps.map((p) => `${p.label} ${p.surface_m2 ?? "?"} m²`).join(", ")}.</p> : null}
            </>
          ) : null}
          <FileDrop
            label="Joindre le justificatif"
            onFiles={async (fs) => {
              setUpErr(null);
              try {
                const id = await uploadJustificatif(orgId, fs[0], "justificatif", f.target === "logement" ? f.property_id || undefined : undefined);
                setDoc({ id, name: fs[0].name });
              } catch (e) {
                setUpErr(errMessage(e));
              }
            }}
          />
          {doc ? <p className="small">Justificatif joint : {doc.name}</p> : null}
          {upErr ? <Notice tone="erreur">{upErr}</Notice> : null}
          <Button
            busy={busy === "add"}
            onClick={async () => {
              const body: any = {
                period_start: f.period_start,
                period_end: f.period_end,
                category: f.category,
                label: f.label,
                amount_cents: amount ?? 0,
                recoverable_cents: recoverable ?? 0,
                distribution_key: f.target === "logement" ? "lot_unique" : f.key,
                document_id: doc?.id,
                note: f.note,
              };
              if (f.target === "logement") body.property_id = f.property_id || null;
              else {
                body.building_id = f.building_id || null;
                if (needWeights) body.weights = buildingProps.filter((p) => weights[p.id] !== undefined && weights[p.id] !== "").map((p) => ({ property_id: p.id, weight: Number(weights[p.id]) }));
              }
              const r = await run("add", () => post(g("/charges/expenses"), body), "Dépense enregistrée et répartie sans perte de centime.");
              if (r) {
                setF({ ...f, label: "" });
                setAmount(null);
                setRecoverable(null);
                setDoc(null);
              }
            }}
          >
            Enregistrer la dépense
          </Button>
          <ErrorBox error={error && !fieldError(error, "label") && !fieldError(error, "amount_cents") ? error : null} />
        </Card>
      ) : null}
      <Card title={`Dépenses ${d.year}`}>
        {d.expenses.length === 0 ? <Empty title="Aucune dépense saisie pour cet exercice" /> : null}
        <ul className="gestion-list">
          {d.expenses.map((e) => (
            <li key={e.id}>
              <div className="gestion-line">
                <span>
                  <strong>{e.label}</strong> · {e.target_label}
                  <span className="block small muted">
                    {d.categories.find((c) => c.value === e.category)?.label ?? e.category} · {date(e.period_start)} – {date(e.period_end)} · clé : {d.keys.find((k) => k.value === e.distribution_key)?.label}
                  </span>
                </span>
                <span className="gestion-actions">
                  <span>
                    <Amount cents={e.recoverable_cents} /> récupérables sur <Amount cents={e.amount_cents} />
                  </span>
                  <DocLink id={e.document_id}>Justificatif</DocLink>
                  {can("finance") ? (
                    <Button variant="discret" small busy={busy === e.id} onClick={() => run(e.id, () => del(g(`/charges/expenses/${e.id}`)), "Dépense supprimée.")}>
                      Supprimer
                    </Button>
                  ) : null}
                </span>
              </div>
              {e.shares.length > 1 ? (
                <p className="small">
                  Répartition : {e.shares.map((s: any) => `${s.label} ${money(s.share_cents)}`).join(" · ")}
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}

function Teom({ d }: { d: Overview }) {
  const { g, can, orgId } = useG();
  const { busy, run, error } = useAction();
  const [f, setF] = useState({ property_id: "", tax_year: String(d.year), note: "" });
  const [teom, setTeom] = useState<number | null>(null);
  const [fees, setFees] = useState<number | null>(0);
  const [doc, setDoc] = useState<{ id: string; name: string } | null>(null);
  const [claim, setClaim] = useState<Record<string, string>>({});
  return (
    <>
      <Notice title="Ce qui est récupérable">
        Seule la taxe d’enlèvement des ordures ménagères est récupérable, sans les frais de gestion ; jamais la taxe foncière entière. Si l’avis couvre plusieurs biens, indiquez la part propre au logement. La TEOM est intégrée à la régularisation, ou demandée séparément pour un bail avec provisions : jamais les deux.
      </Notice>
      {can("finance") ? (
        <Card title="Saisir la TEOM d’un avis de taxe foncière">
          <div className="form-row">
            <Select label="Logement" value={f.property_id} onChange={(e) => setF({ ...f, property_id: e.target.value })} options={[{ value: "", label: "Choisir" }, ...d.properties.map((p) => ({ value: p.id, label: p.label }))]} />
            <Input label="Année d’imposition" type="number" value={f.tax_year} onChange={(e) => setF({ ...f, tax_year: e.target.value })} />
          </div>
          <div className="form-row">
            <MoneyInput label="Montant de TEOM pour ce logement" cents={teom} onChange={setTeom} />
            <MoneyInput label="Dont frais de gestion à exclure" cents={fees} onChange={setFees} error={fieldError(error, "fees_cents")} help="Frais de gestion de la fiscalité locale, s’ils sont inclus dans le montant saisi." />
          </div>
          <p className="small">Montant récupérable : {teom !== null ? money(teom - (fees ?? 0)) : "—"}</p>
          <FileDrop
            label="Joindre l’avis de taxe foncière"
            onFiles={async (fs) => {
              try {
                const id = await uploadJustificatif(orgId, fs[0], "taxe_fonciere", f.property_id || undefined);
                setDoc({ id, name: fs[0].name });
              } catch {
                setDoc(null);
              }
            }}
          />
          {doc ? <p className="small">Avis joint : {doc.name}</p> : null}
          <Button
            busy={busy === "teom"}
            disabled={!f.property_id || !teom}
            onClick={() => run("teom", () => post(g("/charges/teom"), { property_id: f.property_id, tax_year: Number(f.tax_year), teom_cents: teom, fees_cents: fees ?? 0, document_id: doc?.id, note: f.note }), "TEOM enregistrée (frais de gestion exclus).")}
          >
            Enregistrer
          </Button>
        </Card>
      ) : null}
      <Card title={`TEOM ${d.year}`}>
        {d.teom.length === 0 ? <Empty title="Aucune TEOM saisie pour cette année" /> : null}
        <ul className="gestion-list">
          {d.teom.map((t) => {
            const leases = d.leases.filter((l) => l.property_id === t.property_id && l.charges_mode === "provision");
            return (
              <li key={t.id}>
                <div className="gestion-line">
                  <span>
                    <strong>{t.property_label}</strong> · TEOM {t.tax_year} : {money(t.teom_cents)} − frais {money(t.fees_cents)} = <Amount cents={t.recoverable_cents} strong />
                    <span className="block small muted">
                      {t.claimed_via === "regularisation" ? "Intégrée à une régularisation validée" : t.claimed_via === "demande_separee" ? "Demandée séparément au locataire" : "Pas encore réclamée"}
                    </span>
                  </span>
                  <DocLink id={t.document_id}>Avis</DocLink>
                </div>
                {!t.claimed_via && can("finance") && leases.length ? (
                  <div className="form-row">
                    <Select label="Demander séparément au bail" value={claim[t.id] ?? ""} onChange={(e) => setClaim({ ...claim, [t.id]: e.target.value })} options={[{ value: "", label: "Choisir" }, ...leases.map((l) => ({ value: l.id, label: `${l.property_label} — ${l.tenants}` }))]} />
                    <Button variant="secondaire" small disabled={!claim[t.id]} busy={busy === t.id} onClick={() => run(t.id, () => post<any>(g(`/charges/teom/${t.id}/claim`), { lease_id: claim[t.id] }), (r: any) => `${money(r.amount_cents)} ajoutés à la prochaine échéance (${r.days} jours d’occupation sur ${r.year_days}).`)}>
                      Valider la demande
                    </Button>
                  </div>
                ) : null}
                {!t.claimed_via && can("finance") ? (
                  <Button variant="discret" small busy={busy === `d${t.id}`} onClick={() => run(`d${t.id}`, () => del(g(`/charges/teom/${t.id}`)), "TEOM supprimée.")}>
                    Supprimer
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      </Card>
    </>
  );
}

function Regularisations({ d }: { d: Overview }) {
  return (
    <>
      <RegularisationForm leases={d.leases} year={d.year} />
      <Card title="Régularisations">
        {d.regularisations.length === 0 ? <Empty title="Aucune régularisation" /> : null}
        <ul className="gestion-list">
          {d.regularisations.map((r) => (
            <RegularisationItem key={r.id} r={r} />
          ))}
        </ul>
      </Card>
    </>
  );
}

export function RegularisationForm({ leases, year, leaseId: fixedLease, orgId }: { leases: LeaseRef[]; year: number; leaseId?: string; orgId?: string }) {
  const { g, can } = useG(orgId);
  const { busy, run, error } = useAction(orgId);
  const [lease, setLease] = useState(fixedLease ?? "");
  const [period, setPeriod] = useState({ start: `${year}-01-01`, end: `${year}-12-31` });
  const [preview, setPreview] = useState<any>(null);
  const l = leases.find((x) => x.id === lease);
  return (
    <Card title="Calculer une régularisation">
      <div className="form-row">
        {fixedLease ? null : <LeaseSelect leases={leases} value={lease} onChange={(v) => { setLease(v); setPreview(null); }} />}
        <Input label="Du" type="date" value={period.start} onChange={(e) => setPeriod({ ...period, start: e.target.value })} />
        <Input label="Au" type="date" value={period.end} onChange={(e) => setPeriod({ ...period, end: e.target.value })} error={fieldError(error, "period_end")} />
      </div>
      {l?.charges_mode === "forfait" ? <Notice tone="attention">Charges forfaitaires : ni régularisation ni complément ; le forfait se révise seulement dans les conditions du bail.</Notice> : null}
      <div className="gestion-actions">
        <Button variant="secondaire" disabled={!lease} busy={busy === "preview"} onClick={async () => setPreview(await run("preview", () => post(g("/charges/regularisations/preview"), { lease_id: lease, period_start: period.start, period_end: period.end })))}>
          Calculer
        </Button>
        {preview && can("finance") ? (
          <Button busy={busy === "save"} onClick={async () => { const r = await run("save", () => post(g("/charges/regularisations"), { lease_id: lease, period_start: period.start, period_end: period.end }), "Proposition enregistrée : relisez-la puis validez-la."); if (r) setPreview(null); }}>
            Enregistrer la proposition
          </Button>
        ) : null}
      </div>
      {preview ? <RegulDetail r={preview} /> : null}
    </Card>
  );
}

function RegulDetail({ r }: { r: any }) {
  return (
    <div className="stack">
      <Table caption="Détail du calcul">
        <thead>
          <tr>
            <th>Dépense</th>
            <th>Répartition et occupation</th>
            <th className="right">Récupérable</th>
            <th className="right">Part du locataire</th>
          </tr>
        </thead>
        <tbody>
          {r.lines.map((l: any, i: number) => (
            <tr key={i}>
              <td>
                {l.category_label}
                <span className="block small muted">{l.label}</span>
                {l.note ? <span className="block small">{l.note}</span> : null}
              </td>
              <td className="small">
                Part du logement {money(l.lot_share_cents)} · {l.occupied_days} j occupés sur {l.period_days} j
              </td>
              <td className="right">
                <Amount cents={l.recoverable_cents} />
              </td>
              <td className="right">
                <Amount cents={l.tenant_share_cents} />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <KeyValues
        items={[
          ["Charges imputables", money(r.recoverable_total_cents)],
          ["Provisions appelées", money(r.provisions_called_cents)],
          ["dont réglées", money(r.provisions_paid_cents)],
          [r.balance_cents >= 0 ? "Complément à demander" : "Trop-perçu à restituer", <strong key="b">{money(Math.abs(r.balance_cents))}</strong>],
          ["Provision mensuelle proposée", money(r.proposed_provision_cents)],
        ]}
      />
      <ul className="small">
        {r.explanation.map((e: string, i: number) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

export function RegularisationItem({ r, orgId }: { r: any; orgId?: string }) {
  const { g, can } = useG(orgId);
  const { busy, run } = useAction(orgId);
  const [open, setOpen] = useState(false);
  const [applyProvision, setApplyProvision] = useState(true);
  const [provision, setProvision] = useState<number | null>(r.proposed_provision_cents);
  const [effective, setEffective] = useState("");
  const detail = useGet<any>(open ? g(`/charges/regularisations/${r.id}`) : null);
  return (
    <li>
      <div className="gestion-line">
        <span>
          <strong>{r.property_label ?? "Bail"}</strong> · du {date(r.period_start)} au {date(r.period_end)}
          <span className="block small">
            {r.balance_cents >= 0 ? "Complément" : "Trop-perçu"} : <Amount cents={Math.abs(r.balance_cents)} strong /> · {REG_STATUS[r.status] ?? r.status}
          </span>
        </span>
        <span className="gestion-actions">
          <DocLink id={r.document_id}>Décompte PDF</DocLink>
          <Button variant="discret" small onClick={() => setOpen(!open)}>
            {open ? "Masquer" : "Détail"}
          </Button>
          {r.status === "validee" || r.status === "envoyee" ? (
            can("operate") ? (
              <Button variant="secondaire" small busy={busy === "send"} onClick={() => run("send", () => post(g(`/charges/regularisations/${r.id}/send`)), "Décompte envoyé au locataire (envoi simulé).")}>
                {r.status === "envoyee" ? "Renvoyer" : "Envoyer au locataire"}
              </Button>
            ) : null
          ) : null}
          {(r.status === "brouillon" || r.status === "validee") && can("finance") ? (
            <Button variant="discret" small busy={busy === "cancel"} onClick={() => run("cancel", () => post(g(`/charges/regularisations/${r.id}/cancel`)), "Régularisation annulée.")}>
              Annuler
            </Button>
          ) : null}
        </span>
      </div>
      {open && detail.data ? <RegulDetail r={detail.data.computation} /> : null}
      {r.status === "brouillon" && can("finance") ? (
        <div className="gestion-proposal stack-sm">
          <p className="small">Vous validez le calcul affiché : le décompte PDF est produit, le solde est ajouté à la prochaine échéance (ou à rembourser), et la provision peut être ajustée pour l’avenir.</p>
          <Checkbox label="Ajuster la provision mensuelle" checked={applyProvision} onChange={setApplyProvision} />
          {applyProvision ? (
            <div className="form-row">
              <MoneyInput label="Nouvelle provision mensuelle" cents={provision} onChange={setProvision} />
              <Input label="À compter du" type="date" min={today()} value={effective} onChange={(e) => setEffective(e.target.value)} help="Par défaut, le mois prochain." />
            </div>
          ) : null}
          <Button
            busy={busy === "validate"}
            onClick={() =>
              run("validate", () => post(g(`/charges/regularisations/${r.id}/validate`), { apply_provision: applyProvision, proposed_provision_cents: provision, provision_effective_on: effective || undefined }), "Régularisation validée : décompte produit.")
            }
          >
            Valider la régularisation
          </Button>
        </div>
      ) : null}
      {r.status === "annulee" ? <Badge>Annulée</Badge> : null}
    </li>
  );
}
